import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const isIT = pathname === "/it" || pathname.startsWith("/it/");
  const isVendor = pathname === "/vendor" || pathname.startsWith("/vendor/");
  const { user, welcomeUser, clearWelcome } = useAuth();
  const [welcomeName, setWelcomeName] = useState("");

  useEffect(() => {
    if (!welcomeUser) {
      setWelcomeName("");
      return;
    }

    let cancelled = false;

    const loadWelcomeName = async () => {
      if (welcomeUser.role !== "vendor") {
        const name = welcomeUser.name?.trim()?.split(" ")[0] || "";
        if (!cancelled) setWelcomeName(name);
        return;
      }

      try {
        const { data } = await api.get("/vendor/me");
        const storeName =
          data?.business_name?.trim() ||
          welcomeUser.name?.trim()?.split(" ")[0] ||
          "";

        if (!cancelled) setWelcomeName(storeName);
      } catch {
        const fallback = welcomeUser.name?.trim()?.split(" ")[0] || "";
        if (!cancelled) setWelcomeName(fallback);
      }
    };

    loadWelcomeName();

    return () => {
      cancelled = true;
    };
  }, [welcomeUser]);

  useEffect(() => {
    if (!welcomeUser) return;

    const timer = window.setTimeout(() => {
      clearWelcome();
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [welcomeUser, clearWelcome]);

  if (isAdmin || isIT || isVendor) {
    return <main className="min-h-screen bg-[#f3f6f4]">{children}</main>;
  }

  return (
    <div className="nv-site-shell min-h-screen flex flex-col bg-[#FAF3E7]">
      {pathname !== "/" && pathname !== "/login" && !pathname.startsWith("/product/") && pathname !== "/account" && (
        <div className="nv-pattachitra-watermark" aria-hidden="true" />
      )}
      <Navbar />

      {welcomeUser && (
        <div
          className="relative z-40 px-4 pt-4 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="mx-auto max-w-3xl animate-[fadeSlideDown_450ms_ease-out]">
            <div className="bg-white/95 backdrop-blur-sm border border-[#E7E5E4] rounded-2xl shadow-[0_10px_30px_rgba(28,25,23,0.08)] px-5 py-4 flex items-center gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#7C1F30]/10 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-[#7C1F30]" />
              </div>

              <div className="min-w-0">
                <p className="font-serif text-lg text-[#1C1917]">
                  Welcome back{welcomeName ? `, ${welcomeName}` : ""}
                </p>
                <p className="text-xs text-[#78716C] mt-0.5">
                  You’re signed in successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className={
        pathname.startsWith("/product/") ||
        pathname.startsWith("/invoice/") ||
        pathname.startsWith("/payment/") ||
        ["/", "/about", "/login", "/register", "/forgot-password",
         "/reset-password", "/wishlist"].includes(pathname)
          ? "flex-1 pt-[88px] sm:pt-[90px]"
          : "flex-1"
      }>{children}</main>
      <Footer />
    </div>
  );
}
